import React, { useState, useMemo } from 'react';
import { AsYouType } from 'libphonenumber-js';
import { startsWith } from 'lodash';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { TextField } from '@availity/element';

import { useAppContext } from '@/context';

const asYouFormat = (phoneString) => {
  // handle formatting of phone value with AsYouType
  const asYouType = new AsYouType('US');
  return asYouType.input(phoneString);
};

const PhoneField = ({ ...props }) => {
  const { id, label, required, hotpType = 'sms', FieldProps } = props;
  const { register, setValue, formState = {} } = useFormContext();
  const { invalidHotpSmsNumbers, invalidHotpTtsNumbers } = useAppContext();
  const [phone, setPhone] = useState('');

  const { errors = {} } = formState;

  const handleBlur = () => {
    // format phone number on blur
    const formattedValue = asYouFormat(phone);

    setPhone(formattedValue);
    setValue(id, formattedValue);
  };

  const handleChange = (event) => {
    // set phone value onChange
    setPhone(event.target.value);
  };

  const requiredValue = useMemo(() => {
    // output a default required message if only passed in as a bool. else use the string passed in as the error message.
    let output = false;

    if (required) {
      output = typeof required === 'boolean' ? 'Enter a valid US phone number.' : required;
    }

    return output;
  }, [required]);

  return (
    <TextField
      type="tel"
      id={id || 'phone'}
      label={label || 'Phone'}
      {...register(id, {
        onBlur: handleBlur,
        onChange: handleChange,
        required: requiredValue,
        validate: {
          isUSPhoneFormat: (phoneValue) => {
            if (!phoneValue) return true;

            const asYouType = new AsYouType('US');
            asYouType.input(phoneValue);

            return (asYouType.getNumber() && asYouType.getNumber().isPossible()) || 'Enter a valid US phone number.';
          },
          omitCountryCode: (value) => {
            let isValid = true;
            if (value) {
              const hasCountryCode = startsWith(value, '+') || startsWith(value, '1');

              if (hasCountryCode) {
                isValid = false;
              }
            }

            return isValid || 'Enter a valid US phone number. Do not include the country code (+1).';
          },
          notADuplicateSmsDevice: (value) => {
            let isValid = true;
            if (value) {
              const deviceFound =
                hotpType === 'sms'
                  ? invalidHotpSmsNumbers.indexOf(value) >= 0
                  : invalidHotpTtsNumbers.indexOf(value) >= 0;
              if (deviceFound) {
                isValid = false;
              }
            }

            return (
              isValid ||
              'This phone number is already registered with this account. Enter a different number if you want to enroll a second device.'
            );
          },
        },
      })}
      error={!!errors[id]}
      helperText={errors[id]?.message}
      {...FieldProps}
    />
  );
};

PhoneField.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.string,
  hotpType: PropTypes.string,
  FieldProps: PropTypes.object,
};

export default PhoneField;
